import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { getClientFunctions } from "@/lib/firebase-client";
import { httpsCallable } from "firebase/functions";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

type LimitType = "recipeBoxes" | "membersPerBox" | "recipesPerBox";

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null && "message" in error) {
    return String(error.message);
  }
  return "An unknown error occurred";
}

interface UserLimitOverride {
  userId: string;
  limitType: LimitType;
  overrideValue: number | null;
  reason?: string;
  createdAt?: unknown;
  createdBy?: string;
  expiresAt?: unknown;
}

interface UserLimitOverridesDialogProps {
  userId: string;
  userEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserLimitOverridesDialog({
  userId,
  userEmail,
  open,
  onOpenChange,
}: UserLimitOverridesDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState<Record<LimitType, UserLimitOverride | null>>({
    recipeBoxes: null,
    membersPerBox: null,
    recipesPerBox: null,
  });
  const [editingLimit, setEditingLimit] = useState<LimitType | null>(null);
  const [limitValue, setLimitValue] = useState<string>("");
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [reason, setReason] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");

  const loadOverrides = useCallback(async () => {
    try {
      setLoading(true);
      const getUserLimitOverrides = httpsCallable<
        { userId: string },
        { overrides: Record<string, UserLimitOverride> }
      >(getClientFunctions()!, "getUserLimitOverrides");

      const result = await getUserLimitOverrides({ userId });
      
      const overridesMap: Record<LimitType, UserLimitOverride | null> = {
        recipeBoxes: result.data.overrides.recipeBoxes || null,
        membersPerBox: result.data.overrides.membersPerBox || null,
        recipesPerBox: result.data.overrides.recipesPerBox || null,
      };

      setOverrides(overridesMap);
    } catch (error: unknown) {
      console.error("Error loading overrides:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: getErrorMessage(error) || "Failed to load limit overrides"
      });
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    if (open) {
      loadOverrides();
    }
  }, [open, loadOverrides]);

  const handleEdit = (limitType: LimitType) => {
    const override = overrides[limitType];
    setEditingLimit(limitType);
    if (override) {
      setIsUnlimited(override.overrideValue === null);
      setLimitValue(override.overrideValue?.toString() || "");
      setReason(override.reason || "");
    } else {
      setIsUnlimited(false);
      setLimitValue("");
      setReason("");
    }
    setExpiresInDays("");
  };

  const handleSave = async () => {
    if (!editingLimit) return;

    try {
      setLoading(true);
      const setUserLimitOverride = httpsCallable(
        getClientFunctions()!,
        "setUserLimitOverride"
      );

      await setUserLimitOverride({
        userId,
        limitType: editingLimit,
        overrideValue: isUnlimited ? null : parseInt(limitValue) || 0,
        reason: reason.trim() || undefined,
        expiresInDays: expiresInDays ? parseInt(expiresInDays) : undefined,
      });

      toast({
        title: "Success",
        description: "Limit override saved successfully"
      });
      setEditingLimit(null);
      await loadOverrides();
    } catch (error: unknown) {
      console.error("Error saving override:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: getErrorMessage(error) || "Failed to save limit override"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (limitType: LimitType) => {
    try {
      setLoading(true);
      const removeUserLimitOverride = httpsCallable(
        getClientFunctions()!,
        "removeUserLimitOverride"
      );

      await removeUserLimitOverride({ userId, limitType });

      toast({
        title: "Success",
        description: "Limit override removed successfully"
      });
      await loadOverrides();
    } catch (error: unknown) {
      console.error("Error removing override:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: getErrorMessage(error) || "Failed to remove limit override"
      });
    } finally {
      setLoading(false);
    }
  };

  const getLimitTypeLabel = (limitType: LimitType): string => {
    switch (limitType) {
      case "recipeBoxes":
        return "Recipe Boxes";
      case "membersPerBox":
        return "Members Per Box";
      case "recipesPerBox":
        return "Recipes Per Box";
    }
  };

  const renderOverride = (limitType: LimitType) => {
    const override = overrides[limitType];

    if (editingLimit === limitType) {
      return (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">
              {getLimitTypeLabel(limitType)}
            </Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingLimit(null)}
            >
              Cancel
            </Button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={isUnlimited}
                onCheckedChange={setIsUnlimited}
                id={`unlimited-${limitType}`}
              />
              <Label htmlFor={`unlimited-${limitType}`}>Unlimited</Label>
            </div>

            {!isUnlimited && (
              <div className="space-y-2">
                <Label>Limit Value</Label>
                <Input
                  type="number"
                  min="1"
                  value={limitValue}
                  onChange={(e) => setLimitValue(e.target.value)}
                  placeholder="Enter limit value"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Customer requested temporary increase"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Expires In (days, optional)</Label>
              <Input
                type="number"
                min="1"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                placeholder="Leave empty for no expiration"
              />
            </div>

            <Button onClick={handleSave} disabled={loading} className="w-full">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Override
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="space-y-1">
          <p className="font-medium">{getLimitTypeLabel(limitType)}</p>
          {override ? (
            <>
              <p className="text-sm text-muted-foreground">
                Override:{" "}
                <span className="font-semibold">
                  {override.overrideValue === null
                    ? "Unlimited"
                    : override.overrideValue}
                </span>
              </p>
              {override.reason && (
                <p className="text-xs text-muted-foreground">
                  Reason: {override.reason}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Using tier default
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEdit(limitType)}
            disabled={loading}
          >
            {override ? "Edit" : <Plus className="h-4 w-4" />}
          </Button>
          {override && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemove(limitType)}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>User Limit Overrides</DialogTitle>
          <DialogDescription>
            Set custom limits for {userEmail}. These override the global tier limits.
          </DialogDescription>
        </DialogHeader>

        {loading && !editingLimit ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {renderOverride("recipeBoxes")}
            {renderOverride("membersPerBox")}
            {renderOverride("recipesPerBox")}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
